'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { toast } from 'sonner';
import { User, Phone, Mail, MapPin, FileText, Loader2, Send, ChevronRight, ChevronLeft, Globe,  } from 'lucide-react';

import FieldError from './FieldError';
import FormErrorBanner from './FormErrorBanner';
import FormFieldWrapper from './FormFieldWrapper';
import SuccessPanel from './SuccessPanel';

import { SERVICE_TYPES, SERVICE_TYPE_LABELS, getServiceLabel } from '@/lib/validations/lead';

// ────────────────────────────────────────────────────────────
// Types
// ────────────────────────────────────────────────────────────

interface FormValues {
  full_name: string;
  phone: string;
  email: string;
  country: string;
  city: string;
  service_type: string;
  problem_description: string;
}

interface SuccessState {
  lead_id: string;
  service_type: string;
  city: string;
  full_name: string;
}

interface LeadFormProps {
  prefillServiceType?: string;
  prefillCity?: string;
}

// ────────────────────────────────────────────────────────────
// Step definitions
// ────────────────────────────────────────────────────────────

const STEPS = [
  { id: 1, label: 'Service' },
  { id: 2, label: 'Location' },
  { id: 3, label: 'Details' },
  { id: 4, label: 'Contact' },
] as const;

// ────────────────────────────────────────────────────────────
// Phone formatter
// ────────────────────────────────────────────────────────────
function formatPhoneInput(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

// ────────────────────────────────────────────────────────────
// Character counter
// ────────────────────────────────────────────────────────────
function CharCounter({ current, max, warn }: { current: number; max: number; warn: number }) {
  const isWarn = current >= warn;
  const isOver = current > max;
  return (
    <span
      className={`text-xs tabular-nums font-500 transition-colors duration-150 ${
        isOver ? 'text-destructive' : isWarn ? 'text-warning' : 'text-muted-foreground'
      }`}
      aria-live="polite"
      aria-label={`${current} of ${max} characters used`}
    >
      {current}/{max}
    </span>
  );
}

// ────────────────────────────────────────────────────────────
// Step progress indicator
// ────────────────────────────────────────────────────────────
function StepIndicator({ currentStep }: { currentStep: number }) {
  return (
    <div className="flex items-center justify-center gap-0 mb-6" role="list" aria-label="Form progress">
      {STEPS.map((step, idx) => {
        const isCompleted = currentStep > step.id;
        const isActive = currentStep === step.id;
        return (
          <div key={step.id} className="flex items-center" role="listitem">
            <div className="flex flex-col items-center">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-600 transition-all duration-200 ${
                  isCompleted
                    ? 'bg-primary text-white'
                    : isActive
                    ? 'bg-primary text-white ring-4 ring-primary/20' :'bg-muted text-muted-foreground'
                }`}
                aria-current={isActive ? 'step' : undefined}
              >
                {isCompleted ? (
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  step.id
                )}
              </div>
              <span
                className={`mt-1 text-[10px] font-500 transition-colors duration-200 ${
                  isActive ? 'text-primary' : isCompleted ? 'text-primary/70' : 'text-muted-foreground'
                }`}
              >
                {step.label}
              </span>
            </div>
            {idx < STEPS.length - 1 && (
              <div
                className={`w-10 sm:w-16 h-0.5 mb-4 mx-1 transition-colors duration-300 ${
                  currentStep > step.id ? 'bg-primary' : 'bg-border'
                }`}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ────────────────────────────────────────────────────────────
// Service type grid selector
// ────────────────────────────────────────────────────────────

const SERVICE_ICONS: Record<string, string> = {
  plumbing: '🔧',
  hvac: '❄️',
  electrical: '⚡',
  roofing: '🏠',
  landscaping: '🌿',
  'pest-control': '🐛',
  'house-cleaning': '🧹',
  'moving-services': '📦',
  locksmith: '🔑',
  'water-damage-restoration': '💧',
  'foundation-repair': '🏗️',
  'painting-interior': '🖌️',
  'painting-exterior': '🎨',
  'concrete-masonry': '🧱',
  'tree-removal': '🌳',
  'appliance-repair': '🔌',
  'solar-panel-installation': '☀️',
  'home-security': '🔒',
  'garage-door-repair': '🚗',
  'legal-services': '⚖️',
  'accounting-tax': '📊',
  'insurance-consulting': '📋',
};

interface ServiceTypeGridProps {
  value: string;
  onChange: (value: string) => void;
  locked?: boolean;
}

function ServiceTypeGrid({ value, onChange, locked = false }: ServiceTypeGridProps) {
  return (
    <div
      className="grid grid-cols-2 sm:grid-cols-3 gap-2.5"
      role="listbox"
      aria-label="Select service type"
    >
      {SERVICE_TYPES.map((slug) => {
        const isSelected = value === slug;
        return (
          <button
            key={slug}
            type="button"
            role="option"
            aria-selected={isSelected}
            disabled={locked && !isSelected}
            onClick={() => { if (!locked) onChange(slug); }}
            className={`
              flex flex-col items-center gap-1.5 px-3 py-3 rounded-xl border text-center
              transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-primary/30
              ${isSelected
                ? 'border-primary bg-primary/5 ring-2 ring-primary/20 text-primary' :'border-border bg-white hover:border-foreground/30 hover:bg-muted/30 text-foreground'
              }
              ${locked && !isSelected ? 'opacity-40 cursor-default' : 'cursor-pointer'}
            `}
          >
            <span className="text-xl leading-none" aria-hidden="true">
              {SERVICE_ICONS[slug] ?? '🔧'}
            </span>
            <span className="text-xs font-500 leading-tight">
              {SERVICE_TYPE_LABELS[slug]}
            </span>
          </button>
        );
      })}
    </div>
  );
}

// ────────────────────────────────────────────────────────────
// Main LeadForm component
// ────────────────────────────────────────────────────────────

export default function LeadForm({ prefillServiceType, prefillCity }: LeadFormProps = {}) {
  const [currentStep, setCurrentStep] = useState(1);
  const [formError, setFormError] = useState<string | null>(null);
  const [successState, setSuccessState] = useState<SuccessState | null>(null);
  const [sourceUrl, setSourceUrl] = useState<string>('');
  const pageLoadTimeRef = useRef<number>(Date.now());
  const formTopRef = useRef<HTMLDivElement>(null);

  const isServiceLocked = !!prefillServiceType;
  const isCityLocked = !!prefillCity;

  useEffect(() => {
    setSourceUrl(window.location.href);
    pageLoadTimeRef.current = Date.now();
  }, []);

  const {
    register,
    handleSubmit,
    control,
    watch,
    reset,
    setError,
    setValue,
    trigger,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: {
      full_name: '',
      phone: '',
      email: '',
      country: '',
      city: prefillCity || '',
      service_type: prefillServiceType || '',
      problem_description: '',
    },
  });

  useEffect(() => {
    if (prefillServiceType) setValue('service_type', prefillServiceType);
  }, [prefillServiceType, setValue]);

  useEffect(() => {
    if (prefillCity) setValue('city', prefillCity);
  }, [prefillCity, setValue]);

  // If service is prefilled, skip step 1
  useEffect(() => {
    if (prefillServiceType && currentStep === 1) {
      setCurrentStep(2);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const problemDescriptionValue = watch('problem_description') || '';
  const DESCRIPTION_MAX = 2000;
  const DESCRIPTION_WARN = 1800;

  // Scroll to top of form on step change
  const scrollToFormTop = useCallback(() => {
    formTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  // ── Step navigation ────────────────────────────────────────
  const goNext = useCallback(async () => {
    let fieldsToValidate: (keyof FormValues)[] = [];
    if (currentStep === 1) fieldsToValidate = ['service_type'];
    if (currentStep === 2) fieldsToValidate = ['country', 'city'];
    if (currentStep === 3) fieldsToValidate = ['problem_description'];

    const valid = await trigger(fieldsToValidate);
    if (!valid) return;

    setCurrentStep((s) => Math.min(s + 1, 4));
    scrollToFormTop();
  }, [currentStep, trigger, scrollToFormTop]);

  const goBack = useCallback(() => {
    setCurrentStep((s) => Math.max(s - 1, 1));
    scrollToFormTop();
  }, [scrollToFormTop]);

  // ── Form submission ────────────────────────────────────────
  const onSubmit = async (data: FormValues) => {
    setFormError(null);
    const submissionDurationMs = Date.now() - pageLoadTimeRef.current;

    // Combine country + city for the city field sent to backend
    const cityValue = data.country ? `${data.city}, ${data.country}` : data.city;

    let response: Response;
    try {
      response = await fetch('/api/leads', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          full_name: data.full_name,
          phone: data.phone,
          email: data.email,
          city: cityValue,
          service_type: data.service_type,
          problem_description: data.problem_description,
          source_url: sourceUrl,
          submission_duration_ms: submissionDurationMs,
        }),
      });
    } catch {
      setFormError("Network error — couldn't reach our servers. Please check your connection and try again.");
      toast.error('Submission failed — network error.', { description: 'Check your connection and try again.' });
      return;
    }

    let result: {
      ok?: boolean;
      leadId?: string;
      success?: boolean;
      lead_id?: string;
      message?: string;
      error?: string;
      details?: Record<string, string> | string;
      field_errors?: Record<string, string>;
    };

    try {
      result = await response.json();
    } catch {
      setFormError('Unexpected server response. Please try again.');
      return;
    }

    const isSuccess = result.ok === true || result.success === true;
    const leadId = result.leadId || result.lead_id;

    if (isSuccess && leadId) {
      setSuccessState({
        lead_id: leadId,
        service_type: data.service_type,
        city: cityValue,
        full_name: data.full_name,
      });
      return;
    }

    const fieldErrors =
      result.field_errors ||
      (result.details && typeof result.details === 'object' ? result.details : undefined);

    if (fieldErrors) {
      for (const [field, message] of Object.entries(fieldErrors)) {
        setError(field as keyof FormValues, { type: 'server', message });
      }
    }

    if (response.status === 429) {
      setFormError('Too many requests. Please wait a few minutes before submitting again.');
    } else if (response.status === 409) {
      setFormError(result.error || "You've already submitted a request recently. We'll be in touch soon.");
    } else {
      setFormError(result.error || 'Something went wrong. Please try again in a moment.');
    }

    toast.error('Submission failed', { description: result.error || 'Please review the form and try again.' });
  };

  // ── Reset ──────────────────────────────────────────────────
  const handleSubmitAnother = useCallback(() => {
    setSuccessState(null);
    setFormError(null);
    setCurrentStep(prefillServiceType ? 2 : 1);
    reset({
      full_name: '',
      phone: '',
      email: '',
      country: '',
      city: prefillCity || '',
      service_type: prefillServiceType || '',
      problem_description: '',
    });
    pageLoadTimeRef.current = Date.now();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [reset, prefillCity, prefillServiceType]);

  // ── Success state ──────────────────────────────────────────
  if (successState) {
    return (
      <SuccessPanel
        leadId={successState.lead_id}
        serviceType={getServiceLabel(successState.service_type)}
        city={successState.city}
        fullName={successState.full_name}
        onSubmitAnother={handleSubmitAnother}
      />
    );
  }

  // ── Step labels ────────────────────────────────────────────
  const stepTitles: Record<number, string> = {
    1: 'What service do you need?',
    2: 'Where do you need it?',
    3: 'Describe your project',
    4: 'Your contact details',
  };

  const stepSubtitles: Record<number, string> = {
    1: 'Select the type of service you\'re looking for.',
    2: 'Tell us your location so we can find local professionals.',
    3: 'The more detail you share, the better quotes you\'ll receive.',
    4: 'Professionals will use these details to contact you.',
  };

  return (
    <div ref={formTopRef}>
      <StepIndicator currentStep={currentStep} />

      {/* Step heading */}
      <div className="mb-5">
        <h2 className="text-lg font-700 text-foreground leading-snug">
          {stepTitles[currentStep]}
        </h2>
        <p className="text-sm text-muted-foreground mt-0.5">
          {stepSubtitles[currentStep]}
        </p>
      </div>

      <form
        onSubmit={handleSubmit(onSubmit)}
        noValidate
        aria-label="Service request form"
      >
        {formError && (
          <FormErrorBanner message={formError} onDismiss={() => setFormError(null)} />
        )}

        {/* ── STEP 1: Service type ─────────────────────────── */}
        {currentStep === 1 && (
          <div className="space-y-4">
            <Controller
              name="service_type"
              control={control}
              rules={{
                required: 'Please select the service type you need.',
                validate: (v) =>
                  (SERVICE_TYPES as readonly string[]).includes(v) ||
                  'Please select a valid service type from the list.',
              }}
              render={({ field }) => (
                <ServiceTypeGrid
                  value={field.value}
                  onChange={(val) => { field.onChange(val); }}
                  locked={isServiceLocked}
                />
              )}
            />
            <FieldError message={errors.service_type?.message} id="service_type-error" />
          </div>
        )}

        {/* ── STEP 2: Location ─────────────────────────────── */}
        {currentStep === 2 && (
          <div className="space-y-5">
            {/* Country */}
            <FormFieldWrapper>
              <label htmlFor="country" className="form-label">
                Country
                <span className="text-destructive ml-0.5" aria-hidden="true">*</span>
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                  <Globe
                    className={`w-4 h-4 transition-colors duration-150 ${
                      errors.country ? 'text-destructive' : 'text-muted-foreground'
                    }`}
                    aria-hidden="true"
                  />
                </div>
                <input
                  id="country"
                  type="text"
                  autoComplete="country-name"
                  placeholder="e.g. United States"
                  aria-required="true"
                  aria-invalid={!!errors.country}
                  aria-describedby={errors.country ? 'country-error' : undefined}
                  className={`form-input pl-10 ${errors.country ? 'form-input-error' : ''}`}
                  {...register('country', {
                    required: 'Country is required.',
                    minLength: { value: 2, message: 'Enter a valid country name.' },
                    maxLength: { value: 100, message: 'Country name must be under 100 characters.' },
                    validate: (v) => v.trim().length >= 2 || 'Enter a valid country name.',
                  })}
                />
              </div>
              <FieldError message={errors.country?.message} id="country-error" />
            </FormFieldWrapper>

            {/* City */}
            <FormFieldWrapper>
              <label htmlFor="city" className="form-label">
                City
                <span className="text-destructive ml-0.5" aria-hidden="true">*</span>
              </label>
              {!isCityLocked && (
                <p className="form-helper -mt-0.5 mb-1">
                  Enter the city where you need the service performed.
                </p>
              )}
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                  <MapPin
                    className={`w-4 h-4 transition-colors duration-150 ${
                      errors.city ? 'text-destructive' : 'text-muted-foreground'
                    }`}
                    aria-hidden="true"
                  />
                </div>
                <input
                  id="city"
                  type="text"
                  autoComplete="address-level2"
                  placeholder="e.g. Austin"
                  aria-required="true"
                  aria-invalid={!!errors.city}
                  aria-describedby={errors.city ? 'city-error' : undefined}
                  className={`form-input pl-10 ${errors.city ? 'form-input-error' : ''} ${
                    isCityLocked ? 'bg-muted/40 cursor-default opacity-80' : ''
                  }`}
                  {...register('city', {
                    required: 'City is required.',
                    minLength: { value: 2, message: 'Enter a valid city name.' },
                    maxLength: { value: 100, message: 'City name must be under 100 characters.' },
                    validate: (v) => v.trim().length >= 2 || 'Enter a valid city name.',
                  })}
                />
                {isCityLocked && (
                  <div className="absolute inset-y-0 right-0 pr-3.5 flex items-center pointer-events-none">
                    <span className="text-xs text-muted-foreground font-500">Locked</span>
                  </div>
                )}
              </div>
              <FieldError message={errors.city?.message} id="city-error" />
            </FormFieldWrapper>
          </div>
        )}

        {/* ── STEP 3: Problem description ───────────────────── */}
        {currentStep === 3 && (
          <div className="space-y-4">
            <FormFieldWrapper>
              <div className="flex items-center justify-between mb-1.5">
                <label htmlFor="problem_description" className="form-label mb-0">
                  Describe your problem or project
                  <span className="text-destructive ml-0.5" aria-hidden="true">*</span>
                </label>
                <CharCounter
                  current={problemDescriptionValue.length}
                  max={DESCRIPTION_MAX}
                  warn={DESCRIPTION_WARN}
                />
              </div>
              <div className="relative">
                <div className="absolute top-3.5 left-3.5 pointer-events-none">
                  <FileText
                    className={`w-4 h-4 transition-colors duration-150 ${
                      errors.problem_description ? 'text-destructive' : 'text-muted-foreground'
                    }`}
                    aria-hidden="true"
                  />
                </div>
                <textarea
                  id="problem_description"
                  rows={6}
                  placeholder="E.g. My kitchen faucet has been dripping for 3 days and the water pressure is low. I also have a slow drain in the bathroom sink. Looking for someone who can come out this week."
                  aria-required="true"
                  aria-invalid={!!errors.problem_description}
                  aria-describedby={errors.problem_description ? 'problem_description-error' : undefined}
                  className={`form-input pl-10 resize-none leading-relaxed ${
                    errors.problem_description ? 'form-input-error' : ''
                  }`}
                  {...register('problem_description', {
                    required: 'Please describe your problem or project.',
                    minLength: {
                      value: 20,
                      message: "Please provide more detail (at least 20 characters). The more you share, the better quotes you'll receive.",
                    },
                    maxLength: {
                      value: DESCRIPTION_MAX,
                      message: `Description must be under ${DESCRIPTION_MAX} characters.`,
                    },
                    validate: (v) =>
                      v.trim().length >= 20 ||
                      "Please provide more detail (at least 20 characters). The more you share, the better quotes you'll receive.",
                  })}
                />
              </div>
              <FieldError message={errors.problem_description?.message} id="problem_description-error" />
            </FormFieldWrapper>
          </div>
        )}

        {/* ── STEP 4: Contact details ───────────────────────── */}
        {currentStep === 4 && (
          <div className="space-y-5">
            {/* Full name */}
            <FormFieldWrapper>
              <label htmlFor="full_name" className="form-label">
                Full name
                <span className="text-destructive ml-0.5" aria-hidden="true">*</span>
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                  <User
                    className={`w-4 h-4 transition-colors duration-150 ${
                      errors.full_name ? 'text-destructive' : 'text-muted-foreground'
                    }`}
                    aria-hidden="true"
                  />
                </div>
                <input
                  id="full_name"
                  type="text"
                  autoComplete="name"
                  placeholder="Maria Gonzalez"
                  aria-required="true"
                  aria-invalid={!!errors.full_name}
                  aria-describedby={errors.full_name ? 'full_name-error' : undefined}
                  className={`form-input pl-10 ${errors.full_name ? 'form-input-error' : ''}`}
                  {...register('full_name', {
                    required: 'Full name is required.',
                    minLength: { value: 2, message: 'Please enter your full name (at least 2 characters).' },
                    maxLength: { value: 120, message: 'Name must be under 120 characters.' },
                    validate: (v) => v.trim().length >= 2 || 'Please enter your full name (at least 2 characters).',
                  })}
                />
              </div>
              <FieldError message={errors.full_name?.message} id="full_name-error" />
            </FormFieldWrapper>

            {/* Phone */}
            <FormFieldWrapper>
              <label htmlFor="phone" className="form-label">
                Phone number
                <span className="text-destructive ml-0.5" aria-hidden="true">*</span>
              </label>
              <p className="form-helper -mt-0.5 mb-1">
                Professionals will contact you at this number.
              </p>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                  <Phone
                    className={`w-4 h-4 transition-colors duration-150 ${
                      errors.phone ? 'text-destructive' : 'text-muted-foreground'
                    }`}
                    aria-hidden="true"
                  />
                </div>
                <Controller
                  name="phone"
                  control={control}
                  rules={{
                    required: 'Phone number is required.',
                    validate: (v) => {
                      const digits = v.replace(/\D/g, '');
                      if (digits.length < 10) return 'Enter a valid US phone number (10 digits).';
                      if (digits.length > 11) return 'Enter a valid US phone number.';
                      if (digits.length === 11 && digits[0] !== '1') return 'Enter a valid US phone number.';
                      return true;
                    },
                  }}
                  render={({ field }) => (
                    <input
                      id="phone"
                      type="tel"
                      autoComplete="tel"
                      placeholder="(512) 555-0198"
                      aria-required="true"
                      aria-invalid={!!errors.phone}
                      aria-describedby={errors.phone ? 'phone-error' : 'phone-helper'}
                      className={`form-input pl-10 ${errors.phone ? 'form-input-error' : ''}`}
                      value={field.value}
                      onChange={(e) => { field.onChange(formatPhoneInput(e.target.value)); }}
                      onBlur={field.onBlur}
                    />
                  )}
                />
              </div>
              <FieldError message={errors.phone?.message} id="phone-error" />
            </FormFieldWrapper>

            {/* Email */}
            <FormFieldWrapper>
              <label htmlFor="email" className="form-label">
                Email address
                <span className="text-destructive ml-0.5" aria-hidden="true">*</span>
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                  <Mail
                    className={`w-4 h-4 transition-colors duration-150 ${
                      errors.email ? 'text-destructive' : 'text-muted-foreground'
                    }`}
                    aria-hidden="true"
                  />
                </div>
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  placeholder="maria@email.com"
                  aria-required="true"
                  aria-invalid={!!errors.email}
                  aria-describedby={errors.email ? 'email-error' : undefined}
                  className={`form-input pl-10 ${errors.email ? 'form-input-error' : ''}`}
                  {...register('email', {
                    required: 'Email address is required.',
                    pattern: {
                      value: /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/,
                      message: 'Enter a valid email address.',
                    },
                    maxLength: { value: 254, message: 'Email address is too long.' },
                  })}
                />
              </div>
              <FieldError message={errors.email?.message} id="email-error" />
            </FormFieldWrapper>

            <p className="text-xs text-muted-foreground">
              <span className="text-destructive font-600">*</span> Required fields
            </p>
          </div>
        )}

        {/* ── Navigation buttons ────────────────────────────── */}
        <div className={`flex gap-3 mt-6 ${currentStep > 1 ? 'justify-between' : 'justify-end'}`}>
          {currentStep > 1 && (
            <button
              type="button"
              onClick={goBack}
              className="flex items-center gap-1.5 px-5 py-3 rounded-xl border border-border bg-white text-sm font-600 text-foreground hover:bg-muted/40 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-primary/30"
            >
              <ChevronLeft className="w-4 h-4" aria-hidden="true" />
              Back
            </button>
          )}

          {currentStep < 4 ? (
            <button
              type="button"
              onClick={goNext}
              className="flex items-center gap-1.5 px-6 py-3 rounded-xl bg-primary text-white text-sm font-600 hover:bg-primary/90 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-primary/30 shadow-sm ml-auto"
            >
              Continue
              <ChevronRight className="w-4 h-4" aria-hidden="true" />
            </button>
          ) : (
            <button
              type="submit"
              disabled={isSubmitting}
              className="btn-primary flex-1 text-base py-4 shadow-btn-primary"
              aria-label={isSubmitting ? 'Submitting your request…' : 'Submit request for free quotes'}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin flex-shrink-0" aria-hidden="true" />
                  <span>Submitting your request…</span>
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
                  <span>Get Free Quotes</span>
                </>
              )}
            </button>
          )}
        </div>

        {/* Privacy note — only on last step */}
        {currentStep === 4 && (
          <p className="text-center text-xs text-muted-foreground leading-relaxed mt-4">
            By submitting, you agree to be contacted by local service professionals.
            We never sell your information to third parties.{' '}
            <a
              href="#"
              className="underline underline-offset-2 hover:text-foreground transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-primary rounded"
              aria-label="Read our privacy policy"
            >
              Privacy Policy
            </a>
          </p>
        )}
      </form>
    </div>
  );
}